import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  Alert,
  Switch,
  Divider,
} from '@mui/material';
import toast from 'react-hot-toast';
import { saveEnabledDepartmentsConfig } from '../api/configApi';
import { getUsers } from '../api/userApi';
import { useQuery } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import { getDepartmentAccent } from '../constants/departmentBoard';
import { normalizeDepartment } from '../constants/departments';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ReorderableList from './ui/ReorderableList';
import { getStoredDepartmentView } from './DepartmentSwitcher';
import {
  CONFIGURABLE_NAV,
  NAV_LABEL_MAX,
  defaultNavForDepartment,
  defaultWorkflowForDepartment,
  mergeWorkflowConfig,
  normalizeNavOrder,
} from '../constants/departmentWorkflow';
import { useEnabledDepartments } from '../hooks/useEnabledDepartments';
import { useDepartmentWorkflow } from '../hooks/useDepartmentWorkflow';

export default function DepartmentSettingsPanel() {
  const qc = useQueryClient();
  const { catalog, enabledDepartments, departmentRows, isLoading, isError } =
    useEnabledDepartments();
  const { workflow: loadedWorkflow, isLoading: workflowLoading } = useDepartmentWorkflow();
  const [selected, setSelected] = useState([]);
  const [workflowDraft, setWorkflowDraft] = useState({});
  const [catalogDraft, setCatalogDraft] = useState([]);
  const [newDeptName, setNewDeptName] = useState('');

  useEffect(() => {
    setCatalogDraft(departmentRows.map((d) => ({ ...d })));
  }, [departmentRows]);


  useEffect(() => {
    setSelected([...enabledDepartments]);
  }, [enabledDepartments]);

  useEffect(() => {
    setWorkflowDraft(mergeWorkflowConfig(loadedWorkflow || {}));
  }, [loadedWorkflow]);

  const saveMutation = useMutation({
    mutationFn: (payload) => saveEnabledDepartmentsConfig(payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['enabled-departments'] });
      toast.success('Department settings saved');
      setSelected(data.enabled || []);
      if (data.workflow) setWorkflowDraft(mergeWorkflowConfig(data.workflow));
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to save department settings');
    },
  });

  const dirty = useMemo(() => {
    const a = [...selected].map(normalizeDepartment).sort().join('|');
    const b = [...enabledDepartments].map(normalizeDepartment).sort().join('|');
    const wfA = JSON.stringify(workflowDraft);
    const wfB = JSON.stringify(mergeWorkflowConfig(loadedWorkflow || {}));
    const catA = JSON.stringify(catalogDraft);
    const catB = JSON.stringify(departmentRows);
    return a !== b || wfA !== wfB || catA !== catB;
  }, [selected, enabledDepartments, workflowDraft, loadedWorkflow, catalogDraft, departmentRows]);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers().then((r) => r.data),
  });
  const managerOptions = useMemo(() => {
    const rows = Array.isArray(usersData) ? usersData : usersData?.users || [];
    return rows.filter((u) =>
      ['manager', 'hod', 'admin', 'superadmin', 'product_manager'].includes(
        String(u.role || '').toLowerCase(),
      ),
    );
  }, [usersData]);

  const catalogFor = (dept) =>
    catalogDraft.find((d) => normalizeDepartment(d.key) === normalizeDepartment(dept)) || {
      key: dept,
      displayName: dept,
      managerId: null,
      builtIn: true,
    };

  const updateCatalogField = (dept, field, value) => {
    const d = normalizeDepartment(dept);
    setCatalogDraft((prev) =>
      prev.map((row) => (normalizeDepartment(row.key) === d ? { ...row, [field]: value } : row)),
    );
  };

  const addDepartment = () => {
    const name = newDeptName.trim();
    if (!name) return;
    if (catalogDraft.some((d) => d.key.toLowerCase() === name.toLowerCase())) {
      toast.error(`${name} already exists`);
      return;
    }
    // The typed name becomes the permanent key AND the initial display name.
    setCatalogDraft((prev) => [
      ...prev,
      { key: name, displayName: name, managerId: null, builtIn: false },
    ]);
    setSelected((prev) => [...prev, name]);
    setNewDeptName('');
    toast.success(`${name} added — save to apply`);
  };

  /**
   * Honour the department switcher: when one is picked, configure just that
   * one. The full catalog is still saved — the filter only narrows what is
   * rendered, so an unlisted department keeps its settings untouched.
   */
  const departmentView = normalizeDepartment(getStoredDepartmentView() || '');
  const visibleCatalog = useMemo(
    () =>
      departmentView
        ? catalog.filter((d) => normalizeDepartment(d) === departmentView)
        : catalog,
    [catalog, departmentView],
  );

  const toggle = (dept) => {
    const d = normalizeDepartment(dept);
    setSelected((prev) => {
      if (prev.map(normalizeDepartment).includes(d)) {
        if (prev.length <= 1) {
          toast.error('At least one department must remain enabled');
          return prev;
        }
        return prev.filter((x) => normalizeDepartment(x) !== d);
      }
      return [...prev, d];
    });
  };

  const updateWorkflowField = (dept, field, value) => {
    const d = normalizeDepartment(dept);
    setWorkflowDraft((prev) => ({
      ...prev,
      [d]: {
        ...(prev[d] || defaultWorkflowForDepartment(d)),
        [field]: value,
      },
    }));
  };

  /** Path -> catalog entry, for rendering a row from its key. */
  const navItemByPath = useMemo(
    () => new Map(CONFIGURABLE_NAV.map((n) => [n.path, n])),
    [],
  );

  /** Rows render in the saved order, so it reflects what users will see. */
  const orderedNavFor = (wf) =>
    normalizeNavOrder(wf?.navOrder).map((path) => navItemByPath.get(path)).filter(Boolean);

  const setNavOrder = (dept, nextOrder) => {
    const d = normalizeDepartment(dept);
    setWorkflowDraft((prev) => {
      const current = prev[d] || defaultWorkflowForDepartment(d);
      return { ...prev, [d]: { ...current, navOrder: normalizeNavOrder(nextOrder) } };
    });
  };

  const updateNavField = (dept, path, field, value) => {
    const d = normalizeDepartment(dept);
    setWorkflowDraft((prev) => {
      const current = prev[d] || defaultWorkflowForDepartment(d);
      const nav = current.nav || defaultNavForDepartment(d);
      const entry = nav[path] || { visible: true, label: path };
      return {
        ...prev,
        [d]: {
          ...current,
          nav: { ...nav, [path]: { ...entry, [field]: value } },
        },
      };
    });
  };

  if (isLoading || workflowLoading) {
    return <Typography color="text.secondary">Loading department settings…</Typography>;
  }

  if (isError) {
    return <Alert severity="error">Could not load department settings.</Alert>;
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
        Visible departments
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
        Choose which departments appear in onboarding, the department switcher, and project or user
        assignment pickers. Disabled departments stay in the system for existing users and data.
      </Typography>

      {departmentView ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing <strong>{departmentView}</strong> only, matching the department switcher.
          Switch to <strong>All departments</strong> to configure the rest.
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        {visibleCatalog.map((dept) => {
          const accent = getDepartmentAccent(dept);
          const checked = selected.map(normalizeDepartment).includes(normalizeDepartment(dept));
          const wf = workflowDraft[normalizeDepartment(dept)] || defaultWorkflowForDepartment(dept);
          return (
            <Box
              key={dept}
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: checked ? accent.border : 'divider',
                bgcolor: checked ? accent.bg : 'background.paper',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => toggle(dept)}
                    sx={{ color: accent.color, '&.Mui-checked': { color: accent.color } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight={700}>{catalogFor(dept).displayName || dept}</Typography>
                    {catalogFor(dept).displayName !== dept && (
                      <Typography variant="caption" color="text.secondary">
                        ({dept})
                      </Typography>
                    )}
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: accent.bg,
                        color: accent.color,
                        border: `1px solid ${accent.border}`,
                      }}
                    >
                      {checked ? 'Visible' : 'Hidden'}
                    </Box>
                  </Box>
                }
                sx={{ mx: 0, mb: checked ? 1 : 0 }}
              />
              {checked && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 4.5, mb: 2, alignItems: 'center' }}>
                    <TextField
                      label="Shown as"
                      size="small"
                      value={catalogFor(dept).displayName || ''}
                      onChange={(e) => updateCatalogField(dept, 'displayName', e.target.value)}
                      inputProps={{ maxLength: 40 }}
                      helperText="Display name only — the internal key never changes"
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      label="Department manager"
                      size="small"
                      select
                      SelectProps={{ native: true }}
                      /* A native select never leaves MUI's label in the
                         unshrunk state, so without this the label sits on top
                         of the selected value instead of on the border. */
                      InputLabelProps={{ shrink: true }}
                      value={catalogFor(dept).managerId ?? ''}
                      onChange={(e) =>
                        updateCatalogField(dept, 'managerId', e.target.value ? Number(e.target.value) : null)
                      }
                      sx={{ minWidth: 220 }}
                    >
                      <option value="">— None —</option>
                      {managerOptions.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name || u.email}
                        </option>
                      ))}
                    </TextField>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 4.5, alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={wf.sprintsEnabled !== false}
                          onChange={(e) => updateWorkflowField(dept, 'sprintsEnabled', e.target.checked)}
                        />
                      }
                      label="Sprint cycles enabled"
                    />
                    <TextField
                      label="Cycle name (Sprint / Phase)"
                      size="small"
                      value={wf.cycleLabel || ''}
                      onChange={(e) => updateWorkflowField(dept, 'cycleLabel', e.target.value)}
                      sx={{ minWidth: 160 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4.5, mt: 1 }}>
                    When sprints are off: sidebar shows Daily Notebook; Sprint Board hides sprint carousel.
                    When sprints are on: Daily Notebook lives inside Sprint Board for the selected sprint.
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ pl: 4.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Menu items
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Tick to show in the sidebar, and rename it for this department. This controls
                      what is <strong>shown</strong> — who may open a page is still decided by Role
                      Permissions.
                    </Typography>

                    <ReorderableList
                      order={orderedNavFor(wf).map((n) => n.path)}
                      onReorder={(next) => setNavOrder(dept, next)}
                      gap={6}
                      renderItem={(path, ctx) => {
                        const item = navItemByPath.get(path);
                        if (!item) return null;
                        const entry = wf.nav?.[item.path] || { visible: true, label: item.label };
                        return (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40 }}
                          >
                            <DragIndicatorIcon
                              fontSize="small"
                              sx={{ color: 'text.disabled', cursor: 'grab' }}
                              aria-hidden="true"
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton
                                size="small"
                                disabled={ctx.isFirst}
                                onClick={ctx.moveUp}
                                aria-label={`Move ${item.label} up`}
                                sx={{ p: 0.15 }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={ctx.isLast}
                                onClick={ctx.moveDown}
                                aria-label={`Move ${item.label} down`}
                                sx={{ p: 0.15 }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Checkbox
                              size="small"
                              checked={entry.visible !== false}
                              onChange={(e) => updateNavField(dept, item.path, 'visible', e.target.checked)}
                              sx={{ p: 0.5, color: accent.color, '&.Mui-checked': { color: accent.color } }}
                              inputProps={{ 'aria-label': `Show ${item.label} for ${dept}` }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                width: 168,
                                flexShrink: 0,
                                color: entry.visible === false ? 'text.disabled' : 'text.primary',
                              }}
                            >
                              {item.label}
                            </Typography>
                            <TextField
                              size="small"
                              value={entry.label ?? ''}
                              placeholder={item.label}
                              disabled={entry.visible === false}
                              onChange={(e) => updateNavField(dept, item.path, 'label', e.target.value)}
                              inputProps={{
                                'aria-label': `Rename ${item.label} for ${dept}`,
                                maxLength: NAV_LABEL_MAX,
                              }}
                              sx={{ flex: 1, maxWidth: 260 }}
                            />
                          </Box>
                        );
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {!departmentView && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
          <TextField
            label="New department"
            size="small"
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDepartment();
              }
            }}
            inputProps={{ maxLength: 40 }}
            placeholder="e.g. Finance"
            sx={{ minWidth: 240 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addDepartment}
            disabled={!newDeptName.trim()}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Add
          </Button>
          <Typography variant="caption" color="text.secondary">
            The name you type becomes its permanent internal key. Built-in
            departments can be relabelled or disabled, but never removed.
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        disabled={!dirty || saveMutation.isPending}
        onClick={() =>
          saveMutation.mutate({
            enabled: selected.map(normalizeDepartment),
            workflow: workflowDraft,
            departments: catalogDraft,
          })
        }
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save department settings'}
      </Button>
    </Paper>
  );
}
